import { useCallback, useMemo, useRef } from 'react';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { useSessionStorageValue } from 'src/app/shared/hooks/storage';

import { TASKS_CONFIGS } from '../../../../constants';
import { PERSONALIZED_TASKS_GENERATION_STATUSES } from '../../constants';
import type { UseDynamicTasksReturn } from '../../types';
import { useDynamicCheckboxAction } from '../useDynamicCheckboxAction';
import { useDynamicTaskAction } from '../useDynamicTaskAction';
import { usePollPersonalizedTasks } from '../usePollPersonalizedTasks';

import { DEFAULT_TASKS_DISPLAY_COUNT, DYNAMIC_TASKS_EXPANDED_STORAGE_KEY, getToggleTaskListData } from './model';
import { buildTaskShapes, getDisplayedTasks, getTaskStats, parsePersonalizedTasks } from './taskShapes';
import { type PendingCtaSync, usePendingPersonalizedTaskSync } from './usePendingPersonalizedTaskSync';

/**
 * Hook that provides dynamic (AI-generated) tasks data.
 */
export function useDynamicTasks(): UseDynamicTasksReturn {
	const {
		data,
		actions: { refreshData },
	} = useStarterDashboardV2PageData();
	const pendingCtaSyncRef = useRef<PendingCtaSync | null>(null);
	const clearPendingCtaSync = useCallback(() => {
		pendingCtaSyncRef.current = null;
	}, []);

	usePendingPersonalizedTaskSync(data.petition, pendingCtaSyncRef, clearPendingCtaSync, refreshData);

	const initialResult = parsePersonalizedTasks(data.petition.personalizedTasks);

	const shouldPoll =
		initialResult.generationStatus === PERSONALIZED_TASKS_GENERATION_STATUSES.PENDING ||
		initialResult.generationStatus === PERSONALIZED_TASKS_GENERATION_STATUSES.IN_PROGRESS;

	const polledData = usePollPersonalizedTasks({ slug: data.petition.slug, shouldPoll });

	const parsedTasks = useMemo(() => {
		return polledData ? parsePersonalizedTasks(polledData) : parsePersonalizedTasks(data.petition.personalizedTasks);
	}, [polledData, data.petition]);

	const { generationStatus, tasks } = parsedTasks;

	const getCtaAction = useDynamicTaskAction();
	const getCheckboxAction = useDynamicCheckboxAction();
	const allTasks = useMemo(
		() =>
			buildTaskShapes(tasks).map((task) => {
				const rawCta = getCtaAction(task.featureKey, task.id, task.status);
				return {
					...task,
					onCtaClick: rawCta
						? async () => {
								const completed = await rawCta();
								const fk = task.featureKey;
								if (fk && Object.hasOwn(TASKS_CONFIGS, fk)) {
									pendingCtaSyncRef.current = { personalizedTaskId: task.id, featureKey: fk };
								}
								refreshData();
								return completed;
							}
						: undefined,
					onCheckboxChange: getCheckboxAction(task.featureKey, task.id, task.title),
				};
			}),
		[tasks, getCtaAction, getCheckboxAction, refreshData],
	);

	const [expandedStorageValue, setExpandedStorageValue] = useSessionStorageValue(DYNAMIC_TASKS_EXPANDED_STORAGE_KEY);
	const taskListIsExpanded = expandedStorageValue === 'true';
	const displayCount = taskListIsExpanded ? allTasks.length : DEFAULT_TASKS_DISPLAY_COUNT;

	const { nextExpandedStorageValue, ...toggleData } = getToggleTaskListData(allTasks, taskListIsExpanded);
	const displayedTasks = useMemo(() => getDisplayedTasks(allTasks, displayCount), [allTasks, displayCount]);
	const stats = useMemo(() => getTaskStats(allTasks), [allTasks]);

	return {
		data: { tasks: displayedTasks, stats, generationStatus, taskListIsExpanded, ...toggleData },
		actions: { toggleTaskList: () => setExpandedStorageValue(nextExpandedStorageValue) },
	};
}
